import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useAuthHeaders } from "@/hooks/use-auth-headers";
import { config } from "@/config";
import { apiFetch } from "@/lib/api-client";
import { toast } from "sonner";
import { NODE_COUNTS_QUERY_KEY_ROOT } from "./use-get-node-counts";

export interface StreamIngestSqlPayload {
  // One or more files. SQL ingest expects exactly one .sql; ES ingest accepts
  // any number of .json files (mapping + settings, multiple indices, etc.).
  // All files in a single call must be the same kind — the backend rejects
  // mixed SQL/JSON batches.
  files: File[];
  projectUuid: string;
  dataLakeId: string;
  llmPlatform?: string;
}

export function useStreamIngestSql() {
  const { getAuthHeaders } = useAuthHeaders();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: StreamIngestSqlPayload) => {
      // Strip Content-Type — the browser must set the multipart boundary itself.
      const headers = { ...getAuthHeaders() } as Record<string, string>;
      delete headers["Content-Type"];
      delete headers["content-type"];

      const llmPlatform = payload.llmPlatform ?? config.llmPlatform;
      const query = `?projectUuid=${encodeURIComponent(payload.projectUuid)}${llmPlatform ? `&llmPlatform=${encodeURIComponent(llmPlatform)}` : ""}`;
      const url = `${config.isometricApiUrl}/db-ontology/stream-ingest${query}`;

      const form = new FormData();
      for (const file of payload.files) {
        form.append("file", file);
      }
      form.append("projectUuid", payload.projectUuid);
      form.append("dataLakeId", payload.dataLakeId);

      const response = await apiFetch(url, {
        method: "POST",
        headers,
        body: form,
      });

      if (!response.ok) {
        let errorMessage = `Failed to upload file: ${response.statusText}`;
        try {
          const errJson = await response.json();
          if (errJson?.message) errorMessage = errJson.message;
        } catch {
          const text = await response.text();
          if (text) errorMessage = text;
        }
        throw new Error(errorMessage);
      }

      const text = await response.text();
      try {
        return text ? JSON.parse(text) : null;
      } catch {
        return text;
      }
    },
    onSuccess: (_, variables) => {
      // Stream ingest is async — invalidate all DDL queries for this data lake
      // so they refetch as the backend populates nodes.
      ["DDLTable", "DDLColumn", "DDLView", "DDLProcedure", "DDLIndex", "DDLConstraint"].forEach(
        (label) => {
          queryClient.invalidateQueries({
            queryKey: ["db-ontology", label, variables.dataLakeId],
          });
        }
      );
      // Also re-pull the DataLake node so its ingestionStatus flips to
      // 'ingesting' as soon as the generator calls back.
      queryClient.invalidateQueries({
        queryKey: ["neo4j-graph", variables.projectUuid],
      });
      queryClient.invalidateQueries({
        queryKey: ingestionStatusQueryKey(variables.projectUuid, variables.dataLakeId),
      });
      // Short confirmation only — the per-DataLake inline banner carries the
      // ongoing "Analyzing…" state so the user sees it on the panel they
      // uploaded from, and NOT when they switch to a different DataLake.
      toast.success("Upload accepted — analyzing in background.");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to upload file");
    },
  });
}

// ── DataLake ingestion status (poll) ──────────────────────────────────────
//
// The hosted code-ontology-generator parses the uploaded .sql / .json file
// out-of-band (minutes for a large Oracle dump) and the backend writes
// `ingestionStatus` ('ingesting' | 'active' | 'error') + `ingestionError` on
// the DataLake node (db-ontology-graph.service.ts:setIngestionStatus). This
// hook polls that one node so the UI can show "Analyzing…" immediately after
// upload and auto-refresh the tables/views/procedures panels when it flips
// to 'active'. The architectural_specs response already carries these props,
// but we poll the single DataLake endpoint to keep the refresh cheap.

export type DataLakeIngestionStatus =
  | "ingesting"
  | "active"
  | "error"
  | null
  | undefined;

export interface DataLakeIngestionState {
  status: DataLakeIngestionStatus;
  error: string | null;
}

export const ingestionToastId = (dataLakeId: string) =>
  `datalake-ingest-${dataLakeId}`;

export const ingestionStatusQueryKey = (
  projectUuid: string,
  dataLakeId: string,
) => ["datalake-ingestion-status", projectUuid, dataLakeId];

export function useDataLakeIngestionStatus(
  projectUuid: string | undefined,
  dataLakeId: string | undefined,
  // Initial status from the already-loaded architectural specs. Lets the hook
  // decide whether to poll on first mount without a round-trip.
  initialStatus?: DataLakeIngestionStatus,
): DataLakeIngestionState {
  const { getAuthHeaders } = useAuthHeaders();
  const queryClient = useQueryClient();
  const previousStatusRef = useRef<DataLakeIngestionStatus>(initialStatus);

  const enabled = !!projectUuid && !!dataLakeId;

  const query = useQuery<DataLakeIngestionState>({
    queryKey:
      projectUuid && dataLakeId
        ? ingestionStatusQueryKey(projectUuid, dataLakeId)
        : ["datalake-ingestion-status", "noop"],
    queryFn: async () => {
      const headers = getAuthHeaders();
      const url =
        `${config.isometricApiUrl}/architectural-graph/${projectUuid}/DataLake` +
        `?filters[id][$eq]=${encodeURIComponent(dataLakeId!)}&limit=1`;
      const response = await apiFetch(url, {
        headers: { "Content-Type": "application/json", ...headers },
      });
      if (!response.ok) {
        throw new Error(
          `Failed to fetch DataLake ingestion status: ${response.statusText}`,
        );
      }
      const json = await response.json();
      const node = Array.isArray(json?.data) ? json.data[0] : undefined;
      return {
        status: (node?.ingestionStatus ?? null) as DataLakeIngestionStatus,
        error: node?.ingestionError ?? null,
      };
    },
    enabled,
    // Poll only while a parse is in flight. 'active' / 'error' are terminal —
    // stop polling to spare the backend; a fresh upload re-invalidates and
    // starts a new poll cycle (see useStreamIngestSql.onSuccess).
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      return s === "ingesting" ? 5000 : false;
    },
    refetchIntervalInBackground: false,
    staleTime: 0,
  });

  // Transition side effects: when status flips off 'ingesting', dismiss the
  // loading toast, show the terminal state, and invalidate the DDL queries
  // so the Tables/Views/Procedures panels pick up the parsed rows.
  useEffect(() => {
    if (!dataLakeId) return;
    const next = query.data?.status;
    const prev = previousStatusRef.current;
    if (prev === next) return;
    previousStatusRef.current = next;

    if (prev === "ingesting" && next === "active") {
      // No toast — the inline banner disappearing + the counts jumping from
      // "0 of 0" to real numbers is signal enough on the panel the user
      // uploaded from. A global toast here would fire on panels the user
      // may have already moved past.
      ["DDLTable", "DDLColumn", "DDLView", "DDLProcedure", "DDLIndex", "DDLConstraint"].forEach(
        (label) => {
          queryClient.invalidateQueries({
            queryKey: ["db-ontology", label, dataLakeId],
          });
        },
      );
      if (projectUuid) {
        queryClient.invalidateQueries({ queryKey: ["neo4j-graph", projectUuid] });
        // Dashboard Architecture / Code tiles — refresh so the DDL/ES/NoSQL
        // nodes the ingest just landed are reflected without a manual Refresh
        // (BREEZEAI-855). Backend already applies per-child deltas during
        // ingest; this re-fetch picks the updated cached count up.
        queryClient.invalidateQueries({
          queryKey: [NODE_COUNTS_QUERY_KEY_ROOT, projectUuid],
        });
      }
    } else if (prev === "ingesting" && next === "error") {
      // Keep the error toast — a failed parse is actionable, and the user
      // may not be on the DataLake panel when the status flips.
      toast.error(query.data?.error || "SQL analysis failed.", {
        id: ingestionToastId(dataLakeId),
        duration: 8000,
      });
    }
  }, [query.data?.status, query.data?.error, dataLakeId, projectUuid, queryClient]);

  return {
    status: query.data?.status ?? initialStatus,
    error: query.data?.error ?? null,
  };
}

export interface DeleteDbOntologyTablePayload {
  tableId: string;
  projectUuid: string;
  dataLakeId?: string;
  llmPlatform?: string;
}

export type DbOntologyLabel =
  | "DDLTable"
  | "DDLColumn"
  | "DDLConstraint"
  | "DDLIndex"
  | "DDLView"
  | "DDLProcedure"
  | "DDLSequence";

export interface UpdateDbOntologyNodePayload {
  label: DbOntologyLabel;
  id: string;
  projectUuid: string;
  data: Record<string, any>;
  dataLakeId?: string;
  llmPlatform?: string;
}

export interface DbOntologyColumn {
  id?: string;
  name: string;
  type: string;
  nullable?: boolean;
  primaryKey?: boolean;
  foreignKey?: boolean;
  unique?: boolean;
  indexed?: boolean;
  description?: string;
  tableId?: string;
  tableName?: string;
  ordinalPosition?: number;
  defaultValue?: string;
}

export interface DbOntologyTable {
  id?: string;
  name: string;
  description?: string;
  columns: DbOntologyColumn[];
  columnCount?: number;
}

export interface DbOntologyView {
  id?: string;
  name: string;
  schema?: string;
  viewType?: string;
  definition?: string;
  description?: string;
  columns?: string[];
}

export interface DbOntologyProcedure {
  id?: string;
  name: string;
  schema?: string;
  procedureType?: string;
  parameters?: string;
  returnType?: string;
  /** Routine source. Stored truncated to 1000 chars by the backend. */
  body?: string;
  description?: string;
}

export interface DbOntologyIndex {
  id?: string;
  name: string;
  tableId?: string;
  columns?: string[];
  isUnique?: boolean;
  indexType?: string;
}

export interface DbOntologyConstraint {
  id?: string;
  name: string | null;
  constraintType?: "PRIMARY_KEY" | "FOREIGN_KEY" | "UNIQUE" | "CHECK" | string;
  tableId?: string;
  columns?: string[];
}

const toBool = (v: any): boolean | undefined => {
  if (v === undefined || v === null) return undefined;
  if (typeof v === "boolean") return v;
  if (typeof v === "string") return ["true", "yes", "y", "1"].includes(v.toLowerCase());
  if (typeof v === "number") return v !== 0;
  return undefined;
};

const formatType = (rawType: string, length: any): string => {
  const t = String(rawType ?? "").trim();
  if (!t) return "";
  if (length === undefined || length === null || length === "") return t;
  const n = Number(length);
  if (!Number.isFinite(n) || n <= 0) return t;
  if (/\(\s*\d/.test(t)) return t;
  return `${t}(${n})`;
};

const normalizeColumn = (raw: any): DbOntologyColumn => {
  if (typeof raw === "string") {
    return { name: raw, type: "" };
  }
  // Graph-node payloads come back as `{ id, label, properties: {...} }`.
  // Fields in `properties` take precedence; top-level `id` remains the node id.
  const props =
    raw?.properties && typeof raw.properties === "object" ? raw.properties : null;
  const src = props ? { ...raw, ...props } : raw;
  const id = raw?.id ?? src?.id ?? src?.columnId ?? src?.column_id ?? src?._id;
  const name =
    src?.name ?? src?.column_name ?? src?.columnName ?? src?.field ?? src?.column ?? "";
  const rawType =
    src?.type ?? src?.data_type ?? src?.dataType ?? src?.columnType ?? src?.dtype ?? "";
  const length = src?.length ?? src?.size ?? src?.maxLength;
  const type = formatType(rawType, length);
  const nullable = toBool(src?.nullable ?? src?.is_nullable ?? src?.isNullable);
  // Backend stores the flag as `isPrimaryKey` (DDLColumnGraph). Keep aliases for legacy shapes.
  const primaryKey = toBool(
    src?.isPrimaryKey ?? src?.is_primary_key ?? src?.primaryKey ?? src?.primary_key ?? src?.pk
  );
  const foreignKey = toBool(src?.isForeignKey ?? src?.is_foreign_key ?? src?.foreignKey);
  const unique = toBool(src?.isUnique ?? src?.is_unique ?? src?.unique);
  const indexed = toBool(src?.isIndexed ?? src?.is_indexed ?? src?.indexed);
  const description = src?.comment ?? src?.description ?? src?.desc ?? "";
  const tableId = src?.tableId ?? src?.table_id;
  const tableName = src?.tableName ?? src?.table_name ?? src?.table;
  const ordinalPosition =
    typeof src?.ordinalPosition === "number" ? src.ordinalPosition : undefined;
  const defaultValue =
    src?.defaultValue !== undefined && src?.defaultValue !== null
      ? String(src.defaultValue)
      : undefined;

  return {
    ...(id ? { id: String(id) } : {}),
    name: String(name ?? ""),
    type,
    ...(nullable !== undefined ? { nullable } : {}),
    ...(primaryKey !== undefined ? { primaryKey } : {}),
    ...(foreignKey !== undefined ? { foreignKey } : {}),
    ...(unique !== undefined ? { unique } : {}),
    ...(indexed !== undefined ? { indexed } : {}),
    ...(description ? { description: String(description) } : {}),
    ...(tableId ? { tableId: String(tableId) } : {}),
    ...(tableName ? { tableName: String(tableName) } : {}),
    ...(ordinalPosition !== undefined ? { ordinalPosition } : {}),
    ...(defaultValue !== undefined ? { defaultValue } : {}),
  };
};

const normalizeTable = (raw: any, fallbackName?: string): DbOntologyTable => {
  if (typeof raw === "string") {
    return { name: raw, columns: [] };
  }
  const props =
    raw?.properties && typeof raw.properties === "object" ? raw.properties : null;
  const src = props ? { ...raw, ...props } : raw;
  const id = raw?.id ?? src?.id ?? src?.tableId ?? src?.table_id ?? src?._id;
  const name =
    src?.name ?? src?.table_name ?? src?.tableName ?? src?.table ?? fallbackName ?? "";
  const description =
    src?.comment ?? src?.description ?? src?.desc ?? "";
  const cols =
    raw?.columns ??
    raw?.fields ??
    raw?.children ??
    raw?.childrenTree ??
    src?.columns ??
    src?.fields ??
    [];
  const columnCount =
    typeof src?.columnCount === "number"
      ? src.columnCount
      : typeof src?.column_count === "number"
      ? src.column_count
      : undefined;
  let columnList: any[] = [];
  if (Array.isArray(cols)) {
    columnList = cols;
  } else if (cols && typeof cols === "object") {
    columnList = Object.entries(cols).map(([k, v]) =>
      typeof v === "object" && v !== null ? { name: k, ...v } : { name: k, type: String(v) }
    );
  }
  return {
    ...(id ? { id: String(id) } : {}),
    name: String(name ?? ""),
    ...(description ? { description: String(description) } : {}),
    columns: columnList.map(normalizeColumn),
    ...(columnCount !== undefined ? { columnCount } : {}),
  };
};

const unwrap = (payload: any): any => {
  if (!payload) return null;
  let raw: any = payload;
  if (typeof raw === "string") {
    try {
      raw = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return raw;
};

export const normalizeDbOntologyResponse = (payload: any): DbOntologyTable[] => {
  const raw = unwrap(payload);
  if (!raw) return [];

  const candidates = [
    raw?.tables,
    raw?.data?.tables,
    raw?.result?.tables,
    raw?.data,
    raw?.result,
    raw,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate.map((t) => normalizeTable(t));
    }
    if (candidate && typeof candidate === "object") {
      // A paginated envelope like `{ data: [...], total: N }` reaches this
      // branch — skip the "every value is a table" heuristic below or it will
      // wrap the whole envelope as a fake table.
      if ("total" in candidate && ("data" in candidate || "items" in candidate)) {
        continue;
      }
      const keys = Object.keys(candidate);
      const looksLikeTable =
        "columns" in candidate || "fields" in candidate;
      if (looksLikeTable) {
        return [normalizeTable(candidate)];
      }
      const everyValueIsTable = keys.every(
        (k) => candidate[k] && typeof candidate[k] === "object"
      );
      if (everyValueIsTable && keys.length > 0) {
        return keys.map((k) => normalizeTable(candidate[k], k));
      }
    }
  }
  return [];
};

export const normalizeDbOntologyColumns = (payload: any): DbOntologyColumn[] => {
  const raw = unwrap(payload);
  if (!raw) return [];

  const candidates = [
    raw?.columns,
    raw?.data?.columns,
    raw?.result?.columns,
    raw?.data,
    raw?.result,
    raw,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate.map(normalizeColumn);
    }
    if (candidate && typeof candidate === "object" && !Array.isArray(candidate)) {
      const looksLikeColumn =
        "type" in candidate ||
        "data_type" in candidate ||
        "dataType" in candidate ||
        "columnType" in candidate;
      if (looksLikeColumn) {
        return [normalizeColumn(candidate)];
      }
    }
  }
  return [];
};

export interface GetDbOntologyTablesOptions {
  // Required by the API: the caller must be a member of this project and the
  // data lake must belong to it. The query stays disabled until it is set.
  projectUuid?: string;
  limit?: number;
  page?: number;
}

export interface DbOntologyTablesPage {
  items: DbOntologyTable[];
  total: number;
}

export function useGetDbOntologyTables(
  dataLakeId: string | undefined,
  enabled: boolean = true,
  options: GetDbOntologyTablesOptions = {}
) {
  const { getAuthHeaders } = useAuthHeaders();
  const { projectUuid, limit, page } = options;

  return useQuery({
    queryKey: [
      "db-ontology",
      "DDLTable",
      dataLakeId,
      limit ?? null,
      page ?? null,
    ],
    queryFn: async (): Promise<DbOntologyTablesPage> => {
      const headers = getAuthHeaders();
      const params = new URLSearchParams();
      if (limit !== undefined) params.set("limit", String(limit));
      if (page !== undefined) params.set("page", String(page));
      // Force a stable, user-friendly sort. Backend default is
      // `createdAt DESC` which is not stable when many rows share a bulk-
      // insert timestamp — SKIP/LIMIT then repeats or drops rows across pages.
      params.set("sortName", "name");
      params.set("sortOrder", "asc");
      if (projectUuid) params.set("projectUuid", projectUuid);
      const query = params.toString();
      const url = `${config.isometricApiUrl}/db-ontology/${encodeURIComponent(
        dataLakeId || ""
      )}/DDLTable${query ? `?${query}` : ""}`;

      const response = await apiFetch(url, { method: "GET", headers });

      if (!response.ok) {
        throw new Error(`Failed to fetch db ontology tables: ${response.statusText}`);
      }

      const text = await response.text();
      let parsed: any = text;
      try {
        parsed = text ? JSON.parse(text) : null;
      } catch {
        parsed = text;
      }

      const items = normalizeDbOntologyResponse(parsed);
      const total = extractTotal(parsed, items.length);
      return { items, total };
    },
    enabled: enabled && !!dataLakeId && !!projectUuid,
    staleTime: 60000,
    retry: 1,
    placeholderData: (prev) => prev,
  });
}

export interface CreateDbOntologyTablePayload {
  projectUuid: string;
  dataLakeId: string;
  name: string;
  schema?: string;
  tableType?: string;
  comment?: string;
  ddlText?: string;
  llmPlatform?: string;
}

export function useCreateDbOntologyTable() {
  const { getAuthHeaders } = useAuthHeaders();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateDbOntologyTablePayload) => {
      const headers = getAuthHeaders();
      const { llmPlatform: payloadLlmPlatform, ...body } = payload;
      const llmPlatform = payloadLlmPlatform ?? config.llmPlatform;
      const query = llmPlatform
        ? `?llmPlatform=${encodeURIComponent(llmPlatform)}`
        : "";
      const url = `${config.isometricApiUrl}/db-ontology/table${query}`;

      const response = await apiFetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        let errorMessage = `Failed to create table: ${response.statusText}`;
        try {
          const errJson = await response.json();
          if (errJson?.message) errorMessage = errJson.message;
        } catch {
          const text = await response.text();
          if (text) errorMessage = text;
        }
        throw new Error(errorMessage);
      }

      const text = await response.text();
      try {
        return text ? JSON.parse(text) : null;
      } catch {
        return text;
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["db-ontology", "DDLTable", variables.dataLakeId],
      });
      toast.success("Table created successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create table");
    },
  });
}

export function useDeleteDbOntologyTable() {
  const { getAuthHeaders } = useAuthHeaders();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: DeleteDbOntologyTablePayload) => {
      const headers = getAuthHeaders();
      const llmPlatform = payload.llmPlatform ?? config.llmPlatform;
      const query = `?projectUuid=${encodeURIComponent(payload.projectUuid)}${llmPlatform ? `&llmPlatform=${encodeURIComponent(llmPlatform)}` : ""}`;
      const url = `${config.isometricApiUrl}/db-ontology/table/${encodeURIComponent(
        payload.tableId
      )}${query}`;

      const response = await apiFetch(url, { method: "DELETE", headers });

      if (!response.ok) {
        let errorMessage = `Failed to delete table: ${response.statusText}`;
        try {
          const errJson = await response.json();
          if (errJson?.message) errorMessage = errJson.message;
        } catch {
          const text = await response.text();
          if (text) errorMessage = text;
        }
        throw new Error(errorMessage);
      }

      const text = await response.text();
      try {
        return text ? JSON.parse(text) : null;
      } catch {
        return text;
      }
    },
    onSuccess: (_, variables) => {
      if (variables.dataLakeId) {
        queryClient.invalidateQueries({
          queryKey: ["db-ontology", "DDLTable", variables.dataLakeId],
        });
        queryClient.invalidateQueries({
          queryKey: ["db-ontology", "DDLColumn", variables.dataLakeId],
        });
      }
      queryClient.invalidateQueries({
        queryKey: [NODE_COUNTS_QUERY_KEY_ROOT, variables.projectUuid],
      });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete table");
    },
  });
}

export function useUpdateDbOntologyNode() {
  const { getAuthHeaders } = useAuthHeaders();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: UpdateDbOntologyNodePayload) => {
      const headers = getAuthHeaders();
      const llmPlatform = payload.llmPlatform ?? config.llmPlatform;
      const query = `?projectUuid=${encodeURIComponent(payload.projectUuid)}${llmPlatform ? `&llmPlatform=${encodeURIComponent(llmPlatform)}` : ""}`;
      const url = `${config.isometricApiUrl}/db-ontology/${encodeURIComponent(
        payload.label
      )}/${encodeURIComponent(payload.id)}${query}`;

      const response = await apiFetch(url, {
        method: "PUT",
        headers,
        body: JSON.stringify(payload.data ?? {}),
      });

      if (!response.ok) {
        let errorMessage = `Failed to update ${payload.label}: ${response.statusText}`;
        try {
          const errJson = await response.json();
          if (errJson?.message) errorMessage = errJson.message;
        } catch {
          const text = await response.text();
          if (text) errorMessage = text;
        }
        throw new Error(errorMessage);
      }

      const text = await response.text();
      try {
        return text ? JSON.parse(text) : null;
      } catch {
        return text;
      }
    },
    onSuccess: async (_, variables) => {
      if (variables.dataLakeId) {
        // Refetch (not just invalidate) the label that was updated so the
        // new values are in cache before the dialog closes — otherwise the
        // panel + dialog can render the old row until a hard reload.
        await queryClient.refetchQueries({
          queryKey: ["db-ontology", variables.label, variables.dataLakeId],
        });
        // Constraint / index edits (and column PK toggles that mutate a
        // PRIMARY_KEY constraint via this generic node updater) can also
        // change the table + column projections — mark the common adjacent
        // labels stale so every panel stays in sync on next paint.
        const alsoInvalidate: DbOntologyLabel[] = [
          "DDLTable",
          "DDLColumn",
          "DDLConstraint",
          "DDLIndex",
        ];
        for (const label of alsoInvalidate) {
          if (label === variables.label) continue;
          queryClient.invalidateQueries({
            queryKey: ["db-ontology", label, variables.dataLakeId],
          });
        }
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to update ontology node");
    },
  });
}

export interface GetDbOntologyColumnsOptions {
  // Required by the API: the caller must be a member of this project and the
  // data lake must belong to it. The query stays disabled until it is set.
  projectUuid?: string;
  // DDLColumnGraph stores `tableId` (parent DDLTable node id) — use this to scope columns.
  tableId?: string;
  limit?: number;
  page?: number;
}

export function useGetDbOntologyColumns(
  dataLakeId: string | undefined,
  enabled: boolean = true,
  options: GetDbOntologyColumnsOptions = {}
) {
  const { getAuthHeaders } = useAuthHeaders();
  const { projectUuid, tableId, limit, page } = options;

  return useQuery({
    queryKey: [
      "db-ontology",
      "DDLColumn",
      dataLakeId,
      tableId ?? null,
      limit ?? null,
      page ?? null,
    ],
    queryFn: async (): Promise<DbOntologyColumn[]> => {
      const headers = getAuthHeaders();
      const params = new URLSearchParams();
      if (tableId) params.set("filters[tableId][$eq]", tableId);
      if (limit !== undefined) params.set("limit", String(limit));
      if (page !== undefined) params.set("page", String(page));
      if (projectUuid) params.set("projectUuid", projectUuid);
      const query = params.toString();
      const url = `${config.isometricApiUrl}/db-ontology/${encodeURIComponent(
        dataLakeId || ""
      )}/DDLColumn${query ? `?${query}` : ""}`;

      const response = await apiFetch(url, { method: "GET", headers });

      if (!response.ok) {
        throw new Error(`Failed to fetch db ontology columns: ${response.statusText}`);
      }

      const text = await response.text();
      let parsed: any = text;
      try {
        parsed = text ? JSON.parse(text) : null;
      } catch {
        parsed = text;
      }

      return normalizeDbOntologyColumns(parsed);
    },
    enabled: enabled && !!dataLakeId && !!projectUuid,
    staleTime: 60000,
    retry: 1,
  });
}

export interface CreateDbOntologyColumnPayload {
  projectUuid: string;
  dataLakeId: string;
  tableId: string;
  name: string;
  dataType?: string;
  length?: number;
  precision?: number;
  scale?: number;
  nullable?: boolean;
  // Backend field names per DDLColumnGraph
  isPrimaryKey?: boolean;
  isForeignKey?: boolean;
  isUnique?: boolean;
  isIndexed?: boolean;
  defaultValue?: string;
  comment?: string;
  ordinalPosition?: number;
  llmPlatform?: string;
}

export function useCreateDbOntologyColumn() {
  const { getAuthHeaders } = useAuthHeaders();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateDbOntologyColumnPayload) => {
      const headers = getAuthHeaders();
      const {
        llmPlatform: payloadLlmPlatform,
        tableId,
        projectUuid,
        dataLakeId,
        ...body
      } = payload;
      void dataLakeId;
      const llmPlatform = payloadLlmPlatform ?? config.llmPlatform;
      const query = `?projectUuid=${encodeURIComponent(projectUuid)}${llmPlatform ? `&llmPlatform=${encodeURIComponent(llmPlatform)}` : ""}`;
      const url = `${config.isometricApiUrl}/db-ontology/table/${encodeURIComponent(
        tableId
      )}/column${query}`;

      const response = await apiFetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        let errorMessage = `Failed to create column: ${response.statusText}`;
        try {
          const errJson = await response.json();
          if (errJson?.message) errorMessage = errJson.message;
        } catch {
          const text = await response.text();
          if (text) errorMessage = text;
        }
        throw new Error(errorMessage);
      }

      const text = await response.text();
      try {
        return text ? JSON.parse(text) : null;
      } catch {
        return text;
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["db-ontology", "DDLColumn", variables.dataLakeId],
      });
      queryClient.invalidateQueries({
        queryKey: ["db-ontology", "DDLTable", variables.dataLakeId],
      });
      toast.success("Column created successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create column");
    },
  });
}

export interface DeleteDbOntologyColumnPayload {
  columnId: string;
  projectUuid: string;
  dataLakeId?: string;
  llmPlatform?: string;
}

export function useDeleteDbOntologyColumn() {
  const { getAuthHeaders } = useAuthHeaders();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: DeleteDbOntologyColumnPayload) => {
      const headers = getAuthHeaders();
      const llmPlatform = payload.llmPlatform ?? config.llmPlatform;
      const query = `?projectUuid=${encodeURIComponent(payload.projectUuid)}${llmPlatform ? `&llmPlatform=${encodeURIComponent(llmPlatform)}` : ""}`;
      const url = `${config.isometricApiUrl}/db-ontology/column/${encodeURIComponent(
        payload.columnId
      )}${query}`;

      const response = await apiFetch(url, { method: "DELETE", headers });

      if (!response.ok) {
        let errorMessage = `Failed to delete column: ${response.statusText}`;
        try {
          const errJson = await response.json();
          if (errJson?.message) errorMessage = errJson.message;
        } catch {
          const text = await response.text();
          if (text) errorMessage = text;
        }
        throw new Error(errorMessage);
      }

      const text = await response.text();
      try {
        return text ? JSON.parse(text) : null;
      } catch {
        return text;
      }
    },
    onSuccess: (_, variables) => {
      if (variables.dataLakeId) {
        queryClient.invalidateQueries({
          queryKey: ["db-ontology", "DDLColumn", variables.dataLakeId],
        });
        queryClient.invalidateQueries({
          queryKey: ["db-ontology", "DDLTable", variables.dataLakeId],
        });
      }
      queryClient.invalidateQueries({
        queryKey: [NODE_COUNTS_QUERY_KEY_ROOT, variables.projectUuid],
      });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete column");
    },
  });
}

// ------------------------------------------------------------------
// DDLView
// ------------------------------------------------------------------

const normalizeView = (raw: any): DbOntologyView => {
  if (typeof raw === "string") return { name: raw };
  const props =
    raw?.properties && typeof raw.properties === "object" ? raw.properties : null;
  const src = props ? { ...raw, ...props } : raw;
  const id = raw?.id ?? src?.id ?? src?._id;
  const name = src?.name ?? src?.view_name ?? src?.viewName ?? "";
  const schema = src?.schema;
  const viewType = src?.viewType ?? src?.view_type;
  const definition = src?.definition;
  const description = src?.comment ?? src?.description ?? src?.desc ?? "";
  const columns = Array.isArray(src?.columns)
    ? (src.columns as any[]).map((c) => String(c))
    : undefined;
  return {
    ...(id ? { id: String(id) } : {}),
    name: String(name ?? ""),
    ...(schema ? { schema: String(schema) } : {}),
    ...(viewType ? { viewType: String(viewType) } : {}),
    ...(definition ? { definition: String(definition) } : {}),
    ...(description ? { description: String(description) } : {}),
    ...(columns ? { columns } : {}),
  };
};

const normalizeListResponse = <T,>(payload: any, mapItem: (raw: any) => T): T[] => {
  const raw = unwrap(payload);
  if (!raw) return [];
  const candidates = [raw?.data, raw?.result, raw?.items, raw?.nodes, raw];
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate.map(mapItem);
  }
  return [];
};

// Pull the total row count for a paginated list response. Backend returns
// `{ success, data, total }` on /db-ontology/{dataLakeId}/{label} endpoints —
// clients need `total` to size the pagination footer without over-fetching.
const extractTotal = (payload: any, fallback: number): number => {
  const raw = unwrap(payload);
  if (!raw || typeof raw !== "object") return fallback;
  const t =
    typeof raw?.total === "number"
      ? raw.total
      : typeof raw?.data?.total === "number"
      ? raw.data.total
      : typeof raw?.meta?.total === "number"
      ? raw.meta.total
      : typeof raw?.pagination?.total === "number"
      ? raw.pagination.total
      : undefined;
  return typeof t === "number" ? t : fallback;
};

export interface GetDbOntologyViewsOptions {
  // Required by the API: the caller must be a member of this project and the
  // data lake must belong to it. The query stays disabled until it is set.
  projectUuid?: string;
  limit?: number;
  page?: number;
}

export interface DbOntologyViewsPage {
  items: DbOntologyView[];
  total: number;
}

export function useGetDbOntologyViews(
  dataLakeId: string | undefined,
  enabled: boolean = true,
  options: GetDbOntologyViewsOptions = {}
) {
  const { getAuthHeaders } = useAuthHeaders();
  const { projectUuid, limit, page } = options;

  return useQuery({
    queryKey: [
      "db-ontology",
      "DDLView",
      dataLakeId,
      limit ?? null,
      page ?? null,
    ],
    queryFn: async (): Promise<DbOntologyViewsPage> => {
      const headers = getAuthHeaders();
      const params = new URLSearchParams();
      if (limit !== undefined) params.set("limit", String(limit));
      if (page !== undefined) params.set("page", String(page));
      // Stable sort — see useGetDbOntologyTables for why.
      params.set("sortName", "name");
      params.set("sortOrder", "asc");
      if (projectUuid) params.set("projectUuid", projectUuid);
      const query = params.toString();
      const url = `${config.isometricApiUrl}/db-ontology/${encodeURIComponent(
        dataLakeId || ""
      )}/DDLView${query ? `?${query}` : ""}`;

      const response = await apiFetch(url, { method: "GET", headers });
      if (!response.ok) {
        throw new Error(`Failed to fetch db ontology views: ${response.statusText}`);
      }
      const text = await response.text();
      let parsed: any = text;
      try {
        parsed = text ? JSON.parse(text) : null;
      } catch {
        parsed = text;
      }
      const items = normalizeListResponse<DbOntologyView>(parsed, normalizeView);
      const total = extractTotal(parsed, items.length);
      return { items, total };
    },
    enabled: enabled && !!dataLakeId && !!projectUuid,
    staleTime: 60000,
    retry: 1,
    placeholderData: (prev) => prev,
  });
}

export interface DeleteDbOntologyViewPayload {
  viewId: string;
  projectUuid: string;
  dataLakeId?: string;
  llmPlatform?: string;
}

export function useDeleteDbOntologyView() {
  const { getAuthHeaders } = useAuthHeaders();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: DeleteDbOntologyViewPayload) => {
      const headers = getAuthHeaders();
      const llmPlatform = payload.llmPlatform ?? config.llmPlatform;
      const query = `?projectUuid=${encodeURIComponent(payload.projectUuid)}${llmPlatform ? `&llmPlatform=${encodeURIComponent(llmPlatform)}` : ""}`;
      const url = `${config.isometricApiUrl}/db-ontology/view/${encodeURIComponent(
        payload.viewId
      )}${query}`;

      const response = await apiFetch(url, { method: "DELETE", headers });
      if (!response.ok) {
        let errorMessage = `Failed to delete view: ${response.statusText}`;
        try {
          const errJson = await response.json();
          if (errJson?.message) errorMessage = errJson.message;
        } catch {
          const text = await response.text();
          if (text) errorMessage = text;
        }
        throw new Error(errorMessage);
      }
      const text = await response.text();
      try {
        return text ? JSON.parse(text) : null;
      } catch {
        return text;
      }
    },
    onSuccess: (_, variables) => {
      if (variables.dataLakeId) {
        queryClient.invalidateQueries({
          queryKey: ["db-ontology", "DDLView", variables.dataLakeId],
        });
      }
      queryClient.invalidateQueries({
        queryKey: [NODE_COUNTS_QUERY_KEY_ROOT, variables.projectUuid],
      });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete view");
    },
  });
}

export interface CreateDbOntologyViewPayload {
  projectUuid: string;
  dataLakeId: string;
  name: string;
  schema?: string;
  viewType?: "view" | "materialized_view";
  definition?: string;
  // DDLViewGraph requires `columns: string[]` — send [] when unknown.
  columns: string[];
  comment?: string;
  llmPlatform?: string;
}

export function useCreateDbOntologyView() {
  const { getAuthHeaders } = useAuthHeaders();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateDbOntologyViewPayload) => {
      const headers = getAuthHeaders();
      const { llmPlatform: payloadLlmPlatform, ...body } = payload;
      const llmPlatform = payloadLlmPlatform ?? config.llmPlatform;
      const query = llmPlatform
        ? `?llmPlatform=${encodeURIComponent(llmPlatform)}`
        : "";
      const url = `${config.isometricApiUrl}/db-ontology/view${query}`;

      const response = await apiFetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        let errorMessage = `Failed to create view: ${response.statusText}`;
        try {
          const errJson = await response.json();
          if (errJson?.message) errorMessage = errJson.message;
        } catch {
          const text = await response.text();
          if (text) errorMessage = text;
        }
        throw new Error(errorMessage);
      }

      const text = await response.text();
      try {
        return text ? JSON.parse(text) : null;
      } catch {
        return text;
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["db-ontology", "DDLView", variables.dataLakeId],
      });
      toast.success("View created successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create view");
    },
  });
}

// ------------------------------------------------------------------
// DDLProcedure
// ------------------------------------------------------------------

const normalizeProcedure = (raw: any): DbOntologyProcedure => {
  if (typeof raw === "string") return { name: raw };
  const props =
    raw?.properties && typeof raw.properties === "object" ? raw.properties : null;
  const src = props ? { ...raw, ...props } : raw;
  const id = raw?.id ?? src?.id ?? src?._id;
  const name = src?.name ?? src?.procedure_name ?? src?.procedureName ?? "";
  const schema = src?.schema;
  const procedureType = src?.procedureType ?? src?.procedure_type;
  const parameters =
    typeof src?.parameters === "string" ? src.parameters : undefined;
  const returnType = src?.returnType ?? src?.return_type;
  const body = src?.body ?? src?.definition ?? src?.source;
  const description = src?.comment ?? src?.description ?? src?.desc ?? "";
  return {
    ...(id ? { id: String(id) } : {}),
    name: String(name ?? ""),
    ...(schema ? { schema: String(schema) } : {}),
    ...(procedureType ? { procedureType: String(procedureType) } : {}),
    ...(parameters ? { parameters } : {}),
    ...(returnType ? { returnType: String(returnType) } : {}),
    ...(body ? { body: String(body) } : {}),
    ...(description ? { description: String(description) } : {}),
  };
};

export interface GetDbOntologyProceduresOptions {
  // Required by the API: the caller must be a member of this project and the
  // data lake must belong to it. The query stays disabled until it is set.
  projectUuid?: string;
  limit?: number;
  page?: number;
}

export interface DbOntologyProceduresPage {
  items: DbOntologyProcedure[];
  total: number;
}

export function useGetDbOntologyProcedures(
  dataLakeId: string | undefined,
  enabled: boolean = true,
  options: GetDbOntologyProceduresOptions = {}
) {
  const { getAuthHeaders } = useAuthHeaders();
  const { projectUuid, limit, page } = options;

  return useQuery({
    queryKey: [
      "db-ontology",
      "DDLProcedure",
      dataLakeId,
      limit ?? null,
      page ?? null,
    ],
    queryFn: async (): Promise<DbOntologyProceduresPage> => {
      const headers = getAuthHeaders();
      const params = new URLSearchParams();
      if (limit !== undefined) params.set("limit", String(limit));
      if (page !== undefined) params.set("page", String(page));
      // Stable sort — see useGetDbOntologyTables for why.
      params.set("sortName", "name");
      params.set("sortOrder", "asc");
      if (projectUuid) params.set("projectUuid", projectUuid);
      const query = params.toString();
      const url = `${config.isometricApiUrl}/db-ontology/${encodeURIComponent(
        dataLakeId || ""
      )}/DDLProcedure${query ? `?${query}` : ""}`;

      const response = await apiFetch(url, { method: "GET", headers });
      if (!response.ok) {
        throw new Error(`Failed to fetch db ontology procedures: ${response.statusText}`);
      }
      const text = await response.text();
      let parsed: any = text;
      try {
        parsed = text ? JSON.parse(text) : null;
      } catch {
        parsed = text;
      }
      const items = normalizeListResponse<DbOntologyProcedure>(parsed, normalizeProcedure);
      const total = extractTotal(parsed, items.length);
      return { items, total };
    },
    enabled: enabled && !!dataLakeId && !!projectUuid,
    staleTime: 60000,
    retry: 1,
    placeholderData: (prev) => prev,
  });
}

export interface CreateDbOntologyProcedurePayload {
  projectUuid: string;
  dataLakeId: string;
  name: string;
  schema?: string;
  procedureType?: string;
  // Backend CreateDDLProcedureDto.parameters is `any[]` — allow strings or
  // {name, direction, dataType, ...} objects so both dialect forms round-trip.
  parameters?: any[];
  returnType?: string;
  body?: string;
  comment?: string;
  llmPlatform?: string;
}

export function useCreateDbOntologyProcedure() {
  const { getAuthHeaders } = useAuthHeaders();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateDbOntologyProcedurePayload) => {
      const headers = getAuthHeaders();
      const { llmPlatform: payloadLlmPlatform, ...body } = payload;
      const llmPlatform = payloadLlmPlatform ?? config.llmPlatform;
      const query = llmPlatform
        ? `?llmPlatform=${encodeURIComponent(llmPlatform)}`
        : "";
      const url = `${config.isometricApiUrl}/db-ontology/procedure${query}`;

      const response = await apiFetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        let errorMessage = `Failed to create procedure: ${response.statusText}`;
        try {
          const errJson = await response.json();
          if (errJson?.message) errorMessage = errJson.message;
        } catch {
          const text = await response.text();
          if (text) errorMessage = text;
        }
        throw new Error(errorMessage);
      }
      const text = await response.text();
      try {
        return text ? JSON.parse(text) : null;
      } catch {
        return text;
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["db-ontology", "DDLProcedure", variables.dataLakeId],
      });
      toast.success("Procedure created successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create procedure");
    },
  });
}

export interface DeleteDbOntologyProcedurePayload {
  procedureId: string;
  projectUuid: string;
  dataLakeId?: string;
  llmPlatform?: string;
}

export function useDeleteDbOntologyProcedure() {
  const { getAuthHeaders } = useAuthHeaders();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: DeleteDbOntologyProcedurePayload) => {
      const headers = getAuthHeaders();
      const llmPlatform = payload.llmPlatform ?? config.llmPlatform;
      const query = `?projectUuid=${encodeURIComponent(payload.projectUuid)}${llmPlatform ? `&llmPlatform=${encodeURIComponent(llmPlatform)}` : ""}`;
      const url = `${config.isometricApiUrl}/db-ontology/procedure/${encodeURIComponent(
        payload.procedureId
      )}${query}`;

      const response = await apiFetch(url, { method: "DELETE", headers });
      if (!response.ok) {
        let errorMessage = `Failed to delete procedure: ${response.statusText}`;
        try {
          const errJson = await response.json();
          if (errJson?.message) errorMessage = errJson.message;
        } catch {
          const text = await response.text();
          if (text) errorMessage = text;
        }
        throw new Error(errorMessage);
      }
      const text = await response.text();
      try {
        return text ? JSON.parse(text) : null;
      } catch {
        return text;
      }
    },
    onSuccess: (_, variables) => {
      if (variables.dataLakeId) {
        queryClient.invalidateQueries({
          queryKey: ["db-ontology", "DDLProcedure", variables.dataLakeId],
        });
      }
      queryClient.invalidateQueries({
        queryKey: [NODE_COUNTS_QUERY_KEY_ROOT, variables.projectUuid],
      });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete procedure");
    },
  });
}

// ------------------------------------------------------------------
// DDLIndex
// ------------------------------------------------------------------

const normalizeIndex = (raw: any): DbOntologyIndex => {
  if (typeof raw === "string") return { name: raw };
  const props =
    raw?.properties && typeof raw.properties === "object" ? raw.properties : null;
  const src = props ? { ...raw, ...props } : raw;
  const id = raw?.id ?? src?.id ?? src?._id;
  const name = src?.name ?? "";
  const tableId = src?.tableId ?? src?.table_id;
  const columns = Array.isArray(src?.columns)
    ? (src.columns as any[]).map((c) => String(c))
    : undefined;
  const isUnique =
    typeof src?.isUnique === "boolean"
      ? src.isUnique
      : typeof src?.is_unique === "boolean"
      ? src.is_unique
      : undefined;
  const indexType = src?.indexType ?? src?.index_type;
  return {
    ...(id ? { id: String(id) } : {}),
    name: String(name ?? ""),
    ...(tableId ? { tableId: String(tableId) } : {}),
    ...(columns ? { columns } : {}),
    ...(isUnique !== undefined ? { isUnique } : {}),
    ...(indexType ? { indexType: String(indexType) } : {}),
  };
};

export interface GetDbOntologyTableScopedOptions {
  // Required by the API: the caller must be a member of this project and the
  // data lake must belong to it. The query stays disabled until it is set.
  projectUuid?: string;
  tableId?: string;
  limit?: number;
  page?: number;
}

export function useGetDbOntologyIndexes(
  dataLakeId: string | undefined,
  enabled: boolean = true,
  options: GetDbOntologyTableScopedOptions = {}
) {
  const { getAuthHeaders } = useAuthHeaders();
  const { projectUuid, tableId, limit, page } = options;

  return useQuery({
    queryKey: [
      "db-ontology",
      "DDLIndex",
      dataLakeId,
      tableId ?? null,
      limit ?? null,
      page ?? null,
    ],
    queryFn: async (): Promise<DbOntologyIndex[]> => {
      const headers = getAuthHeaders();
      const params = new URLSearchParams();
      if (tableId) params.set("filters[tableId][$eq]", tableId);
      if (limit !== undefined) params.set("limit", String(limit));
      if (page !== undefined) params.set("page", String(page));
      if (projectUuid) params.set("projectUuid", projectUuid);
      const query = params.toString();
      const url = `${config.isometricApiUrl}/db-ontology/${encodeURIComponent(
        dataLakeId || ""
      )}/DDLIndex${query ? `?${query}` : ""}`;
      const response = await apiFetch(url, { method: "GET", headers });
      if (!response.ok) {
        throw new Error(`Failed to fetch db ontology indexes: ${response.statusText}`);
      }
      const text = await response.text();
      let parsed: any = text;
      try {
        parsed = text ? JSON.parse(text) : null;
      } catch {
        parsed = text;
      }
      return normalizeListResponse<DbOntologyIndex>(parsed, normalizeIndex);
    },
    enabled: enabled && !!dataLakeId && !!projectUuid,
    staleTime: 60000,
    retry: 1,
  });
}

export interface CreateDbOntologyIndexPayload {
  projectUuid: string;
  dataLakeId: string;
  tableId: string;
  name: string;
  columns: string[];
  isUnique?: boolean;
  indexType?: "BTREE" | "BITMAP" | "FUNCTION_BASED" | "DOMAIN";
  whereClause?: string;
  tablespace?: string;
  llmPlatform?: string;
}

export function useCreateDbOntologyIndex() {
  const { getAuthHeaders } = useAuthHeaders();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateDbOntologyIndexPayload) => {
      const headers = getAuthHeaders();
      const { llmPlatform: payloadLlmPlatform, tableId, ...body } = payload;
      const llmPlatform = payloadLlmPlatform ?? config.llmPlatform;
      const query = `?projectUuid=${encodeURIComponent(payload.projectUuid)}${llmPlatform ? `&llmPlatform=${encodeURIComponent(llmPlatform)}` : ""}`;
      const url = `${config.isometricApiUrl}/db-ontology/table/${encodeURIComponent(
        tableId
      )}/index${query}`;

      const response = await apiFetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        let errorMessage = `Failed to create index: ${response.statusText}`;
        try {
          const errJson = await response.json();
          if (errJson?.message) errorMessage = errJson.message;
        } catch {
          const text = await response.text();
          if (text) errorMessage = text;
        }
        throw new Error(errorMessage);
      }
      const text = await response.text();
      try {
        return text ? JSON.parse(text) : null;
      } catch {
        return text;
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["db-ontology", "DDLIndex", variables.dataLakeId],
      });
      toast.success("Index created successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create index");
    },
  });
}

export interface DeleteDbOntologyIndexPayload {
  indexId: string;
  projectUuid: string;
  dataLakeId?: string;
  llmPlatform?: string;
}

export function useDeleteDbOntologyIndex() {
  const { getAuthHeaders } = useAuthHeaders();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: DeleteDbOntologyIndexPayload) => {
      const headers = getAuthHeaders();
      const llmPlatform = payload.llmPlatform ?? config.llmPlatform;
      const query = `?projectUuid=${encodeURIComponent(payload.projectUuid)}${llmPlatform ? `&llmPlatform=${encodeURIComponent(llmPlatform)}` : ""}`;
      const url = `${config.isometricApiUrl}/db-ontology/index/${encodeURIComponent(
        payload.indexId
      )}${query}`;

      const response = await apiFetch(url, { method: "DELETE", headers });
      if (!response.ok) {
        let errorMessage = `Failed to delete index: ${response.statusText}`;
        try {
          const errJson = await response.json();
          if (errJson?.message) errorMessage = errJson.message;
        } catch {
          const text = await response.text();
          if (text) errorMessage = text;
        }
        throw new Error(errorMessage);
      }
      const text = await response.text();
      try {
        return text ? JSON.parse(text) : null;
      } catch {
        return text;
      }
    },
    onSuccess: (_, variables) => {
      if (variables.dataLakeId) {
        queryClient.invalidateQueries({
          queryKey: ["db-ontology", "DDLIndex", variables.dataLakeId],
        });
      }
      queryClient.invalidateQueries({
        queryKey: [NODE_COUNTS_QUERY_KEY_ROOT, variables.projectUuid],
      });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete index");
    },
  });
}

// ------------------------------------------------------------------
// DDLConstraint
// ------------------------------------------------------------------

const normalizeConstraint = (raw: any): DbOntologyConstraint => {
  if (typeof raw === "string") return { name: raw };
  const props =
    raw?.properties && typeof raw.properties === "object" ? raw.properties : null;
  const src = props ? { ...raw, ...props } : raw;
  const id = raw?.id ?? src?.id ?? src?._id;
  const name = src?.name ?? null;
  const constraintType = src?.constraintType ?? src?.constraint_type;
  const tableId = src?.tableId ?? src?.table_id;
  const columns = Array.isArray(src?.columns)
    ? (src.columns as any[]).map((c) => String(c))
    : undefined;
  return {
    ...(id ? { id: String(id) } : {}),
    name: name === null ? null : String(name ?? ""),
    ...(constraintType ? { constraintType: String(constraintType) } : {}),
    ...(tableId ? { tableId: String(tableId) } : {}),
    ...(columns ? { columns } : {}),
  };
};

export function useGetDbOntologyConstraints(
  dataLakeId: string | undefined,
  enabled: boolean = true,
  options: GetDbOntologyTableScopedOptions = {}
) {
  const { getAuthHeaders } = useAuthHeaders();
  const { projectUuid, tableId, limit, page } = options;

  return useQuery({
    queryKey: [
      "db-ontology",
      "DDLConstraint",
      dataLakeId,
      tableId ?? null,
      limit ?? null,
      page ?? null,
    ],
    queryFn: async (): Promise<DbOntologyConstraint[]> => {
      const headers = getAuthHeaders();
      const params = new URLSearchParams();
      if (tableId) params.set("filters[tableId][$eq]", tableId);
      if (limit !== undefined) params.set("limit", String(limit));
      if (page !== undefined) params.set("page", String(page));
      if (projectUuid) params.set("projectUuid", projectUuid);
      const query = params.toString();
      const url = `${config.isometricApiUrl}/db-ontology/${encodeURIComponent(
        dataLakeId || ""
      )}/DDLConstraint${query ? `?${query}` : ""}`;
      const response = await apiFetch(url, { method: "GET", headers });
      if (!response.ok) {
        throw new Error(`Failed to fetch db ontology constraints: ${response.statusText}`);
      }
      const text = await response.text();
      let parsed: any = text;
      try {
        parsed = text ? JSON.parse(text) : null;
      } catch {
        parsed = text;
      }
      return normalizeListResponse<DbOntologyConstraint>(parsed, normalizeConstraint);
    },
    enabled: enabled && !!dataLakeId && !!projectUuid,
    staleTime: 60000,
    retry: 1,
  });
}

export interface CreateDbOntologyConstraintPayload {
  projectUuid: string;
  dataLakeId: string;
  tableId: string;
  name?: string | null;
  constraintType: "PRIMARY_KEY" | "FOREIGN_KEY" | "UNIQUE" | "CHECK";
  columns: string[];
  onDelete?: string;
  checkExpression?: string;
  enabled?: boolean;
  validated?: boolean;
  llmPlatform?: string;
}

export function useCreateDbOntologyConstraint() {
  const { getAuthHeaders } = useAuthHeaders();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateDbOntologyConstraintPayload) => {
      const headers = getAuthHeaders();
      const { llmPlatform: payloadLlmPlatform, tableId, ...body } = payload;
      const llmPlatform = payloadLlmPlatform ?? config.llmPlatform;
      const query = `?projectUuid=${encodeURIComponent(payload.projectUuid)}${llmPlatform ? `&llmPlatform=${encodeURIComponent(llmPlatform)}` : ""}`;
      const url = `${config.isometricApiUrl}/db-ontology/table/${encodeURIComponent(
        tableId
      )}/constraint${query}`;

      const response = await apiFetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        let errorMessage = `Failed to create constraint: ${response.statusText}`;
        try {
          const errJson = await response.json();
          if (errJson?.message) errorMessage = errJson.message;
        } catch {
          const text = await response.text();
          if (text) errorMessage = text;
        }
        throw new Error(errorMessage);
      }
      const text = await response.text();
      try {
        return text ? JSON.parse(text) : null;
      } catch {
        return text;
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["db-ontology", "DDLConstraint", variables.dataLakeId],
      });
      // A PRIMARY_KEY/UNIQUE/FK constraint change re-derives the affected
      // columns' flags (isPrimaryKey/isUnique/…) and the table's hasPrimaryKey
      // on the backend — refetch columns + tables so the UI updates without a
      // manual reload (HUBS-160).
      queryClient.invalidateQueries({
        queryKey: ["db-ontology", "DDLColumn", variables.dataLakeId],
      });
      queryClient.invalidateQueries({
        queryKey: ["db-ontology", "DDLTable", variables.dataLakeId],
      });
      toast.success("Constraint created successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create constraint");
    },
  });
}

export interface DeleteDbOntologyConstraintPayload {
  constraintId: string;
  projectUuid: string;
  dataLakeId?: string;
  llmPlatform?: string;
}

export function useDeleteDbOntologyConstraint() {
  const { getAuthHeaders } = useAuthHeaders();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: DeleteDbOntologyConstraintPayload) => {
      const headers = getAuthHeaders();
      const llmPlatform = payload.llmPlatform ?? config.llmPlatform;
      const query = `?projectUuid=${encodeURIComponent(payload.projectUuid)}${llmPlatform ? `&llmPlatform=${encodeURIComponent(llmPlatform)}` : ""}`;
      const url = `${config.isometricApiUrl}/db-ontology/constraint/${encodeURIComponent(
        payload.constraintId
      )}${query}`;

      const response = await apiFetch(url, { method: "DELETE", headers });
      if (!response.ok) {
        let errorMessage = `Failed to delete constraint: ${response.statusText}`;
        try {
          const errJson = await response.json();
          if (errJson?.message) errorMessage = errJson.message;
        } catch {
          const text = await response.text();
          if (text) errorMessage = text;
        }
        throw new Error(errorMessage);
      }
      const text = await response.text();
      try {
        return text ? JSON.parse(text) : null;
      } catch {
        return text;
      }
    },
    onSuccess: (_, variables) => {
      if (variables.dataLakeId) {
        queryClient.invalidateQueries({
          queryKey: ["db-ontology", "DDLConstraint", variables.dataLakeId],
        });
        // Removing a PK constraint clears the affected columns' derived
        // isPrimaryKey flag and the table's hasPrimaryKey — refetch both so the
        // UI updates without a manual reload (HUBS-160).
        queryClient.invalidateQueries({
          queryKey: ["db-ontology", "DDLColumn", variables.dataLakeId],
        });
        queryClient.invalidateQueries({
          queryKey: ["db-ontology", "DDLTable", variables.dataLakeId],
        });
      }
      queryClient.invalidateQueries({
        queryKey: [NODE_COUNTS_QUERY_KEY_ROOT, variables.projectUuid],
      });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete constraint");
    },
  });
}
